import { useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import type { DbPortalImportErgebnis } from '@gql/shared-types';
import { api, API_BASIS } from '../api/client.js';
import { useDatenKontext } from '../daten/kontext.js';
import { leseCsvDatei, ZUORDNUNG_TEXT } from '../daten/standorte.js';

const MAX_BYTES = 2 * 1024 * 1024;

export function StandortImport() {
  const daten = useDatenKontext();
  const gebietName = new Map(daten.gebiete.features.map((f) => [f.properties.slug, f.properties.name]));
  const [csv, setCsv] = useState<string | null>(null);
  const [dateiname, setDateiname] = useState<string | null>(null);
  const [vorschau, setVorschau] = useState<DbPortalImportErgebnis | null>(null);
  const [ergebnis, setErgebnis] = useState<DbPortalImportErgebnis | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ziehen, setZiehen] = useState(false);

  async function dateiGewaehlt(datei: File | undefined) {
    setFehler(null);
    setVorschau(null);
    setErgebnis(null);
    if (!datei) return;
    if (datei.size > MAX_BYTES) {
      setFehler('Die Datei ist größer als 2 MB. Bitte aufteilen.');
      return;
    }
    if (/\.xlsx?$/i.test(datei.name)) {
      setFehler('Bitte die Tabelle in Excel als „CSV UTF-8 (durch Trennzeichen getrennt)“ speichern und diese Datei hochladen.');
      return;
    }
    const text = await leseCsvDatei(datei);
    setCsv(text);
    setDateiname(datei.name);
    await pruefen(text);
  }

  async function pruefen(text: string) {
    setLaeuft(true);
    try {
      setVorschau(await api<DbPortalImportErgebnis>('/standorte/import', { methode: 'POST', text }));
    } catch (f) {
      setFehler((f as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  async function uebernehmen() {
    if (!csv) return;
    setLaeuft(true);
    try {
      setErgebnis(await api<DbPortalImportErgebnis>('/standorte/import?uebernehmen=1', { methode: 'POST', text: csv }));
      setVorschau(null);
    } catch (f) {
      setFehler((f as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  const ablegen = (e: DragEvent) => {
    e.preventDefault();
    setZiehen(false);
    void dateiGewaehlt(e.dataTransfer.files[0]);
  };

  const gueltig = vorschau ? vorschau.neu + vorschau.aktualisiert : 0;

  return (
    <div className="seite">
      <p className="brotkrumen">
        <Link to="/standorte">← Zu den Standorten</Link>
      </p>
      <h1>Standorte importieren</h1>
      <ol className="schritte">
        <li>
          <a href={`${API_BASIS}/standorte-vorlage.csv`} download>
            CSV-Vorlage herunterladen
          </a>{' '}
          und Ihre Standorte eintragen (z. B. aus SAP RE-FX). Pflicht ist nur die Spalte „Standort“; mit PLZ, Ort oder
          Koordinaten ordnet das Portal das Konzessionsgebiet selbst zu.
        </li>
        <li>Datei hier ablegen oder auswählen. Sie sehen zuerst eine Vorschau; gespeichert wird erst nach Ihrer Bestätigung.</li>
      </ol>

      <label
        className={`ablage${ziehen ? ' ist-aktiv' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setZiehen(true);
        }}
        onDragLeave={() => setZiehen(false)}
        onDrop={ablegen}
      >
        <input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => void dateiGewaehlt(e.target.files?.[0])} />
        <span className="ablage-text">
          {dateiname ? `Datei: ${dateiname}` : 'CSV-Datei hierher ziehen oder tippen, um sie auszuwählen'}
        </span>
      </label>

      {fehler && (
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      )}
      {laeuft && (
        <p role="status">
          <span className="spinner" aria-hidden="true" /> Wird verarbeitet …
        </p>
      )}

      {ergebnis && (
        <div className="hinweis-kasten" role="status">
          <p>
            <strong>Übernommen:</strong> {ergebnis.neu} neu, {ergebnis.aktualisiert} aktualisiert
            {ergebnis.fehler ? `, ${ergebnis.fehler} Zeilen mit Fehlern übersprungen` : ''}.
          </p>
          {ergebnis.ohneGebiet > 0 && <p>{ergebnis.ohneGebiet} Standorte ohne Gebiet – GQL ordnet sie zu.</p>}
          <Link to="/standorte" className="knopf">
            Zu den Standorten
          </Link>
        </div>
      )}

      {vorschau && (
        <section aria-labelledby="h-vorschau">
          <h2 id="h-vorschau">Vorschau</h2>
          <p>
            {vorschau.zeilen} Zeilen: {vorschau.neu} neu, {vorschau.aktualisiert} bereits vorhanden (werden aktualisiert),{' '}
            {vorschau.fehler} mit Fehlern{vorschau.ohneGebiet ? `, ${vorschau.ohneGebiet} ohne Gebiet` : ''}.
          </p>
          <div className="tabelle-rahmen">
            <table className="tabelle tabelle-karten">
              <thead>
                <tr>
                  <th scope="col">Zeile</th>
                  <th scope="col">Standort</th>
                  <th scope="col">Ergebnis</th>
                  <th scope="col">Konzessionsgebiet</th>
                  <th scope="col">Hinweise</th>
                </tr>
              </thead>
              <tbody>
                {vorschau.details.map((d) => (
                  <tr key={d.zeile} className={d.aktion === 'fehler' ? 'zeile-fehler' : undefined}>
                    <td data-label="Zeile">{d.zeile}</td>
                    <td data-label="Standort">{d.name ?? '–'}</td>
                    <td data-label="Ergebnis">{d.aktion === 'fehler' ? 'Fehler' : d.aktion === 'neu' ? 'neu' : 'wird aktualisiert'}</td>
                    <td data-label="Gebiet">
                      {d.gebietSlug ? gebietName.get(d.gebietSlug) ?? d.gebietSlug : '–'}
                      {d.zuordnungArt && <div className="zelle-hinweis">{ZUORDNUNG_TEXT[d.zuordnungArt]}</div>}
                    </td>
                    <td data-label="Hinweise">
                      {[...d.fehler, ...d.hinweise].join(' ') || '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="knopfreihe">
            <button type="button" className="knopf" disabled={laeuft || gueltig === 0} onClick={() => void uebernehmen()}>
              {gueltig === 1 ? '1 Standort übernehmen' : `${gueltig} Standorte übernehmen`}
            </button>
            {vorschau.fehler > 0 && <span className="hinweis-klein">Zeilen mit Fehlern werden übersprungen.</span>}
          </div>
        </section>
      )}
    </div>
  );
}
